import { useQuery } from "@apollo/client";

// types
import { DocumentNode } from "graphql/language/ast";

type TResult = {
  loading: boolean;
  error?: any;
  loadMore: () => void;
  nodes?: any[];
  pageInfo?: any;
  totalCount?: number;
  ratedClassCount?: number;
  refetch: () => void;
};

const DEFAULT_PAGE_SIZE = 10;

const emptyFunction = () => {
  // do nothing
};

const useInfiniteQuery = (
  key: string,
  query: DocumentNode,
  options?: any,
  noInfiniteLoading?: boolean
): TResult => {
  const pageSize = options?.variables?.first || DEFAULT_PAGE_SIZE;
  const { data, loading, error, fetchMore, refetch } = useQuery(query, {
    ...options,
    variables: {
      first: pageSize,
      ...options?.variables,
    },
  });

  if (loading)
    return { loading, loadMore: emptyFunction, refetch: emptyFunction };
  if (error || !data)
    return { loading, error, loadMore: emptyFunction, refetch: emptyFunction };

  const { pageInfo, nodes, totalCount, ratedClassCount } = data[key];

  const loadMore = async () => {
    try {
      await fetchMore({
        variables: {
          after: pageInfo.endCursor,
          first: pageSize,
        },
        updateQuery: (
          previousQueryResult,
          nextQueryResult: { fetchMoreResult?: any; variables?: any }
        ) => {
          const {
            fetchMoreResult: nextPageData,
            variables: nextQueryVariables,
          } = nextQueryResult;
          if (noInfiniteLoading) {
            nextPageData[key].pageInfo.hasNextPage = false;
          }
          if (key === "classSearchResults") {
            if (
              previousQueryResult[key]?.pageInfo.endCursor !==
              nextQueryVariables?.after
            )
              return {
                [key]: {
                  pageInfo: previousQueryResult[key]?.pageInfo,
                  nodes: [...previousQueryResult[key].nodes],
                  // eslint-disable-next-line no-underscore-dangle
                  __typename: previousQueryResult[key].__typename,
                },
              };
          }
          // query GetPassClasses has two special fields: totalCount, ratedClassCount
          const specialFieldsPassClasses = {};
          if (key === "pastClasses") {
            Object.assign(specialFieldsPassClasses, {
              totalCount: nextPageData[key].totalCount,
              ratedClassCount: nextPageData[key].ratedClassCount,
            });
          }
          return {
            [key]: {
              pageInfo: nextPageData[key].pageInfo,
              nodes: [
                ...previousQueryResult[key].nodes,
                ...nextPageData[key].nodes,
              ],
              ...specialFieldsPassClasses,
              // eslint-disable-next-line no-underscore-dangle
              __typename: previousQueryResult[key].__typename,
            },
          };
        },
      });
    } catch (err) {
      console.log("loadMore error", err);
    }
  };

  return {
    nodes,
    loadMore,
    pageInfo,
    loading,
    error,
    totalCount,
    ratedClassCount,
    refetch,
  };
};

export default useInfiniteQuery;
